import { useState, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { MarketSubNav } from '../components/shared/MarketSubNav'
import { MemberPicker, NotifySelect } from '../components/shared/AssignControls'
import { useEligibleAssignees } from '../hooks/useEligibleAssignees'
import { validateAssignForm, type NotifyOption } from '../lib/taskAssign'
import { Card, Pill, DataTable, DecisionSummary, SectionLabel } from '../components/ui'
import type { Column } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror GET /api/v1/rivals/deals) ─────────────────────────────────
interface Location {
  id: string
  name: string
}
interface DealRow {
  id: string
  kind: 'promotion' | 'markdown'
  competitor_id: string
  rival: string
  headline: string
  type: string
  category: string | null
  detected_at: string | null
  running_days: number | null
  active: boolean
  sku_count: number | null
  avg_pct_off: number | null
  deepest_pct_off: number | null
  undercut_you: number | null
  source_url: string | null
}
// Immediately-previous menu-snapshot comparison (markdown breadth + you-undercut
// only — promotions are excluded because they carry no snapshot history). The
// `own_menu_reference: 'current'` flag discloses that previous undercut counts
// are measured against the CURRENT own menu, not a historical own price.
interface DealsPrevious {
  collected_at: string
  own_menu_reference: 'current'
  marked_down_skus: number
  undercutting_you: number
  rivals_promoting: number
  by_competitor: Record<string, { marked_down_skus: number; undercut_you: number; categories: number }>
  clusters: Record<string, { count: number; undercut: number }>
}
interface DealsData {
  location: { id: string; name: string }
  own_menu: boolean
  tracked_rivals: number
  summary: {
    deal_events: number
    rivals_promoting: number
    marked_down_skus: number
    active_promotions: number
    deepest_pct_off: number | null
    undercutting_you: number
  }
  categories: string[]
  rivals: { competitor_id: string; name: string }[]
  types: string[]
  deals: DealRow[]
  insights: { title: string; body: string }[]
  truncated: boolean
  observed_at: string | null
  previous: DealsPrevious | null
}

// ─── Formatters (user-local) ─────────────────────────────────────────────────
const fmtInt = (n: number) => n.toLocaleString('en-US')
const fmtDate = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Promo type → tint. Markdown clusters are the hard-number deals (warm); real
// marketing promotions get a muted rose so the two read apart at a glance.
function typeColor(kind: DealRow['kind']) {
  return kind === 'markdown' ? 'var(--warm)' : 'var(--rose)'
}

type SortCol = 'damage' | 'detected_at' | 'rival' | 'type' | 'category' | 'discount' | 'sku_count' | 'undercut_you'

// ─── Competitive-damage model (the page's DEFAULT ranking lens) ───────────────
// A deal's "damage" is the real business hit it lands on this operator, built
// only from fields already on the row:
//   • undercut_you  — how many of YOUR SKUs the rival now prices below you. The
//                     direct hit, so it is weighted heavily.
//   • sku_count     — how broad the markdown is (repeated pressure across a
//                     category). Adds on top of the undercut hit.
//   • deepest_pct_off — depth is a TIEBREAKER only, never the lead signal, so a
//                     very deep cut on a handful of SKUs cannot outrank a broad
//                     markdown that is actively undercutting us.
// Promotions carry no price/undercut data, so they always rank below markdowns.
// With no own-menu on file undercut_you is null → damage degrades to pure SKU
// volume, which is still the right "who is pressing hardest" order.
const UNDERCUT_WEIGHT = 20
function damageScore(d: DealRow): number {
  if (d.kind !== 'markdown') return -1
  return (d.undercut_you ?? 0) * UNDERCUT_WEIGHT + (d.sku_count ?? 0)
}
function byDamage(a: DealRow, b: DealRow): number {
  const s = damageScore(b) - damageScore(a)
  if (s !== 0) return s
  const dp = (b.deepest_pct_off ?? -1) - (a.deepest_pct_off ?? -1)
  if (dp !== 0) return dp
  return String(b.detected_at ?? '').localeCompare(String(a.detected_at ?? ''))
}

export default function DealEffectiveness() {
  const authFetch = useAuthFetch()

  const [locations, setLocations] = useState<Location[]>([])
  const [locationsLoaded, setLocationsLoaded] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [data, setData] = useState<DealsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [rivalFilter, setRivalFilter] = useState<string>('') // competitor_id ('' = all)
  const [typeFilter, setTypeFilter] = useState<string>('') // display type ('' = all)
  const [locOpen, setLocOpen] = useState(false)
  const [rivalOpen, setRivalOpen] = useState(false)
  const [typeOpen, setTypeOpen] = useState(false)

  const [sortCol, setSortCol] = useState<SortCol>('damage')
  const [sortDir, setSortDir] = useState<1 | -1>(-1)
  // How many rows of the lower "work down the log" zone are shown. The head (top
  // 5) is always visible; the continuation starts short so the first screen is a
  // decision workflow, not a full report dump. Show-more / show-all extend it.
  const LOG_STEP = 15
  const [logShown, setLogShown] = useState(LOG_STEP)

  // Assign-a-task dialog state (files against the leading rival competitor).
  const [assignOpen, setAssignOpen] = useState(false)
  const [taskSaving, setTaskSaving] = useState(false)
  const [taskError, setTaskError] = useState<string | null>(null)

  const [toast, setToast] = useState<string | null>(null)
  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3200)
  }

  // Load the org's locations, default to the first.
  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.json())
      .then((d: { locations?: Location[] }) => {
        const locs = d.locations ?? []
        setLocations(locs)
        setLocationsLoaded(true)
        if (locs.length > 0) setSelectedLocation((cur) => cur || locs[0].id)
        else setLoading(false)
      })
      .catch(() => { setLocationsLoaded(true); setError('Could not load your locations.') })
  }, [authFetch])

  const load = useCallback(() => {
    if (!selectedLocation) return
    setLoading(true); setError(null)
    authFetch(`${API}/api/v1/rivals/deals?location_id=${selectedLocation}`)
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: { data: DealsData }) => { setData(d.data); setRivalFilter(''); setTypeFilter(''); setLogShown(LOG_STEP) })
      .catch((e) => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [authFetch, selectedLocation])

  useEffect(() => { load() }, [load])

  const selectedName = useMemo(
    () => locations.find((l) => l.id === selectedLocation)?.name ?? data?.location.name ?? '—',
    [locations, selectedLocation, data]
  )

  const allDeals = data?.deals ?? []
  const sum = data?.summary
  const hasDeals = allDeals.length > 0
  const trackedRivals = data?.tracked_rivals ?? 0
  const ownMenu = !!data?.own_menu
  // The org genuinely has no location on file. This is distinct from a location
  // that has zero tracked rivals or no active deals — absence of a location is
  // NOT evidence of no rival activity, so we must not render zero pressure cards.
  const noLocation = locationsLoaded && locations.length === 0 && !error

  // Rows after the location/rival/type filters, in canonical DAMAGE order —
  // independent of whatever column the user later clicks. The decision summary
  // and the "highest pressure" strip anchor to this so the page's headline
  // recommendation stays stable even while the operator re-sorts the log.
  const damageRanked = useMemo(() => {
    let rows = allDeals
    if (rivalFilter) rows = rows.filter((d) => d.competitor_id === rivalFilter)
    if (typeFilter) rows = rows.filter((d) => d.type === typeFilter)
    return [...rows].sort(byDamage)
  }, [allDeals, rivalFilter, typeFilter])

  // The single deal cluster to review first: the top of the damage order.
  const topThreat = damageRanked[0] ?? null

  // Who is applying the most pressure, at the RIVAL level: total undercut +
  // markdown breadth across every one of that rival's markdown clusters (same
  // weighting as the row score). Answers "which rival is hurting me most".
  const topRival = useMemo(() => {
    const m = new Map<string, { competitor_id: string; name: string; undercut: number; skus: number; cats: Set<string> }>()
    for (const d of damageRanked) {
      if (d.kind !== 'markdown') continue
      const e = m.get(d.competitor_id) ?? { competitor_id: d.competitor_id, name: d.rival, undercut: 0, skus: 0, cats: new Set<string>() }
      e.undercut += d.undercut_you ?? 0
      e.skus += d.sku_count ?? 0
      if (d.category) e.cats.add(d.category)
      m.set(d.competitor_id, e)
    }
    const ranked = [...m.values()].sort(
      (a, b) => (b.undercut * UNDERCUT_WEIGHT + b.skus) - (a.undercut * UNDERCUT_WEIGHT + a.skus)
    )
    return ranked[0] ?? null
  }, [damageRanked])

  // ── Real prior-observation movement (only when the API returned a comparable
  //    previous menu snapshot). All values are observed facts; nothing is
  //    inferred and nothing implies revenue / margin / sales change. ──────────
  const prev = data?.previous ?? null
  const observedAt = data?.observed_at ?? null
  const obsLabel = fmtDate(observedAt)
  const prevLabel = prev ? fmtDate(prev.collected_at) : null
  const ageDays = observedAt ? Math.floor((Date.now() - Date.parse(observedAt)) / 86_400_000) : null
  const stale = ageDays != null && ageDays > 2

  const leadingClusterKey = topThreat && topThreat.kind === 'markdown' && topThreat.category
    ? `${topThreat.competitor_id}|${topThreat.category}` : null
  const prevCluster = leadingClusterKey && prev ? prev.clusters[leadingClusterKey] : undefined
  const breadthChange = prev && sum ? sum.marked_down_skus - prev.marked_down_skus : null
  const undercutChange = prev && sum ? sum.undercutting_you - prev.undercutting_you : null
  const clusterBreadthChange = prevCluster && topThreat ? (topThreat.sku_count ?? 0) - prevCluster.count : null
  const clusterUndercutChange = prevCluster && topThreat ? (topThreat.undercut_you ?? 0) - prevCluster.undercut : null

  const filteredDeals = useMemo(() => {
    let rows = allDeals
    if (rivalFilter) rows = rows.filter((d) => d.competitor_id === rivalFilter)
    if (typeFilter) rows = rows.filter((d) => d.type === typeFilter)
    if (sortCol === 'damage') return [...rows].sort(byDamage)
    const dir = sortDir
    const val = (d: DealRow): string | number => {
      switch (sortCol) {
        case 'rival': return d.rival.toLowerCase()
        case 'type': return d.type.toLowerCase()
        case 'category': return (d.category ?? '').toLowerCase()
        case 'discount': return d.deepest_pct_off ?? -1
        case 'sku_count': return d.sku_count ?? -1
        case 'undercut_you': return d.undercut_you ?? -1
        default: return d.detected_at ?? ''
      }
    }
    return [...rows].sort((a, b) => {
      const va = val(a); const vb = val(b)
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir
      return ((va as number) - (vb as number)) * dir
    })
  }, [allDeals, rivalFilter, typeFilter, sortCol, sortDir])

  function handleSort(col: SortCol) {
    if (col === sortCol) setSortDir((d) => (d === 1 ? -1 : 1))
    else { setSortCol(col); setSortDir(-1) }
  }
  const sortInd = (col: SortCol) => (col === sortCol ? (sortDir === 1 ? ' ▲' : ' ▼') : '')

  // Shared row-click: the existing toast summary, used by both zone tables.
  const onDealClick = (d: DealRow) => showToast(
    d.kind === 'markdown'
      ? `${d.rival}: ${d.headline}${d.avg_pct_off != null ? ` — avg −${d.avg_pct_off}%, deepest −${d.deepest_pct_off}%` : ''}${ownMenu && d.undercut_you ? ` · ${d.undercut_you} undercut you` : ''}`
      : `${d.rival}: ${d.headline}${d.running_days != null ? ` — running ${d.running_days}d` : ''}`
  )

  // The category label the assign action / suggestions key off.
  const leadCategory = topThreat?.category ?? null
  const canAssign = !!(topRival && topThreat && topThreat.kind === 'markdown' && topThreat.competitor_id)

  const submitAssign = async (bodyText: string, opts: { assigneeUserId: string | null; notify: NotifyOption }) => {
    if (!topThreat) return
    setTaskSaving(true); setTaskError(null)
    try {
      const res = await authFetch(`${API}/api/v1/annotations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: bodyText,
          entity_type: 'competitor',
          entity_id: topThreat.competitor_id,
          assignee_id: opts.assigneeUserId,
          notify: opts.notify,
        }),
      })
      const d = await res.json().catch(() => null)
      if (!res.ok) { setTaskError(d?.error || "Couldn't create the task. Nothing was saved — try again."); return }
      setAssignOpen(false)
      showToast(`Task assigned — review ${leadCategory ?? 'pricing'} against ${topThreat.rival}.`)
    } catch {
      setTaskError("Couldn't reach the server. Nothing was saved — try again.")
    } finally {
      setTaskSaving(false)
    }
  }

  // ── Deal log columns ───────────────────────────────────────────────────────
  const columns: Column<DealRow>[] = [
    {
      key: 'detected_at', width: 66, header: <span onClick={() => handleSort('detected_at')} style={{ cursor: 'pointer' }}>Seen{sortInd('detected_at')}</span>,
      render: (d) => <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{fmtDate(d.detected_at)}</span>,
    },
    {
      key: 'rival', width: 168, header: <span onClick={() => handleSort('rival')} style={{ cursor: 'pointer' }}>Rival{sortInd('rival')}</span>,
      mono: false, render: (d) => <span style={{ fontWeight: 700, color: 'var(--rose)' }}>{d.rival}</span>,
    },
    {
      key: 'headline', header: 'Deal',
      mono: false, render: (d) => (
        <span style={{ color: 'var(--text-1)', display: 'inline-block', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }} title={d.headline}>
          {d.headline}
        </span>
      ),
    },
    {
      key: 'type', width: 92, header: <span onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>Type{sortInd('type')}</span>,
      mono: false, render: (d) => {
        const c = typeColor(d.kind)
        return <span style={{ fontFamily: 'var(--mono)', fontSize: 8, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: `${c}22`, color: c, whiteSpace: 'nowrap' }}>{d.type}</span>
      },
    },
    {
      key: 'category', width: 110, header: <span onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>Category{sortInd('category')}</span>,
      mono: false, render: (d) => <span style={{ color: 'var(--text-3)' }}>{d.category ?? '—'}</span>,
    },
    {
      key: 'discount', width: 104, header: <span onClick={() => handleSort('discount')} style={{ cursor: 'pointer' }}>Discount{sortInd('discount')}</span>,
      align: 'right', render: (d) => {
        if (d.kind !== 'markdown' || d.avg_pct_off == null) return <span style={{ color: 'var(--text-3)' }}>—</span>
        return (
          <div style={{ lineHeight: 1.25 }}>
            <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)' }}>−{d.avg_pct_off}%</div>
            {d.deepest_pct_off != null && d.deepest_pct_off !== d.avg_pct_off && (
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>deepest −{d.deepest_pct_off}%</div>
            )}
          </div>
        )
      },
    },
    {
      key: 'sku_count', width: 74, header: <span onClick={() => handleSort('sku_count')} style={{ cursor: 'pointer' }}>SKUs{sortInd('sku_count')}</span>,
      align: 'right', render: (d) => <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: d.sku_count != null ? 'var(--text-2)' : 'var(--text-3)' }}>{d.sku_count != null ? fmtInt(d.sku_count) : '—'}</span>,
    },
    {
      key: 'undercut_you', width: 116, header: <span onClick={() => handleSort('undercut_you')} style={{ cursor: 'pointer' }}>vs You{sortInd('undercut_you')}</span>,
      align: 'right', render: (d) => {
        if (!data?.own_menu || d.undercut_you == null) return <span style={{ color: 'var(--text-3)' }}>—</span>
        if (d.undercut_you === 0) return <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>0</span>
        return <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: 'var(--danger)22', color: 'var(--danger)' }}>{fmtInt(d.undercut_you)} undercut</span>
      },
    },
  ]

  // Dropdown chrome (shared with s22/s23/s25 conventions).
  const ddBtn = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 'var(--r-sm)',
    fontSize: 11, fontWeight: 600, border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
    background: active ? 'var(--accent)' : 'var(--surface)', color: active ? '#fff' : 'var(--text-1)',
    cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
  })
  const ddPop: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 5px)', left: 0, background: 'var(--surface)',
    border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', boxShadow: '0 8px 32px rgba(30,60,80,0.14)',
    zIndex: 300, minWidth: 170, padding: 5, maxHeight: 300, overflowY: 'auto',
  }
  const ddOpt = (sel: boolean): React.CSSProperties => ({
    padding: '7px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
    color: sel ? 'var(--accent)' : 'var(--text-1)', fontWeight: sel ? 700 : 400, whiteSpace: 'nowrap',
  })

  const rivalLabel = rivalFilter ? (data?.rivals.find((r) => r.competitor_id === rivalFilter)?.name ?? 'Rival') : 'All rivals'

  // The decision region renders only when a real populated state exists.
  const showDecision = !error && !noLocation && !!data && hasDeals

  return (
    <div
      onClick={() => { setLocOpen(false); setRivalOpen(false); setTypeOpen(false) }}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', fontFamily: 'var(--sans)', color: 'var(--text-1)', fontSize: 14, overflow: 'hidden' }}
    >
      {/* TOPBAR */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface)' }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>Deal Patterns</div>
          <div data-testid="deals-subtitle" style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase' }}>
            Observed competitor promotion and markdown activity.
          </div>
          <div data-testid="deals-context" style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2 }}>
            What this shows: observed competitor behavior. Not a measure of your outcomes.
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {data && hasDeals && observedAt && (
            <span data-testid="deals-observed" style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
              Observed {obsLabel}
            </span>
          )}
          {data && hasDeals && (
            <Pill tone="neutral" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {fmtInt(sum?.deal_events ?? 0)} deal{(sum?.deal_events ?? 0) === 1 ? '' : 's'} · {fmtInt(trackedRivals)} tracked rival{trackedRivals === 1 ? '' : 's'}
            </Pill>
          )}
        </div>
      </div>

      {/* MARKET NAV TABS */}
      <MarketSubNav />

      {/* FILTER BAR */}
      <div onClick={(e) => e.stopPropagation()} style={{ padding: '8px 28px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Location</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div data-testid="deals-loc-btn" style={ddBtn(true)} onClick={(e) => { e.stopPropagation(); setLocOpen((o) => !o); setRivalOpen(false); setTypeOpen(false) }}>
            {selectedName}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {locOpen && (
            <div style={ddPop}>
              {locations.length === 0
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No locations yet</div>
                : locations.map((l) => (
                  <div key={l.id} style={ddOpt(l.id === selectedLocation)}
                    onClick={() => { setSelectedLocation(l.id); setLocOpen(false) }}>
                    {l.name}{l.id === selectedLocation ? ' ✓' : ''}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Rival</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div data-testid="deals-rival-btn" style={ddBtn(!!rivalFilter)} onClick={(e) => { e.stopPropagation(); setRivalOpen((o) => !o); setLocOpen(false); setTypeOpen(false) }}>
            {rivalLabel}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {rivalOpen && (
            <div data-testid="deals-rival-pop" style={ddPop}>
              {(data?.rivals.length ?? 0) === 0
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No rivals with deals</div>
                : (
                  <>
                    <div style={ddOpt(!rivalFilter)} onClick={() => { setRivalFilter(''); setRivalOpen(false) }}>All rivals{!rivalFilter ? ' ✓' : ''}</div>
                    {data!.rivals.map((r) => (
                      <div key={r.competitor_id} style={ddOpt(r.competitor_id === rivalFilter)}
                        onClick={() => { setRivalFilter(r.competitor_id); setRivalOpen(false) }}>
                        {r.name}{r.competitor_id === rivalFilter ? ' ✓' : ''}
                      </div>
                    ))}
                  </>
                )}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Type</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div data-testid="deals-type-btn" style={ddBtn(!!typeFilter)} onClick={(e) => { e.stopPropagation(); setTypeOpen((o) => !o); setLocOpen(false); setRivalOpen(false) }}>
            {typeFilter || 'All types'}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {typeOpen && (
            <div data-testid="deals-type-pop" style={ddPop}>
              {(data?.types.length ?? 0) === 0
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No types yet</div>
                : (
                  <>
                    <div style={ddOpt(!typeFilter)} onClick={() => { setTypeFilter(''); setTypeOpen(false) }}>All types{!typeFilter ? ' ✓' : ''}</div>
                    {data!.types.map((t) => (
                      <div key={t} style={ddOpt(t === typeFilter)}
                        onClick={() => { setTypeFilter(t); setTypeOpen(false) }}>
                        {t}{t === typeFilter ? ' ✓' : ''}
                      </div>
                    ))}
                  </>
                )}
            </div>
          )}
        </div>

        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          {data && hasDeals
            ? <>{sum?.deepest_pct_off != null && <><span style={{ color: 'var(--accent)', fontWeight: 700 }}>−{sum.deepest_pct_off}%</span> deepest · </>}<span style={{ fontWeight: 700, color: 'var(--text-2)' }}>{fmtInt(filteredDeals.length)}</span> deal{filteredDeals.length === 1 ? '' : 's'} shown</>
            : ''}
        </div>
      </div>

      {/* MAIN SCROLL */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '14px 28px', minHeight: 0 }}>

        {/* NO LOCATION — a location is required before any rival comparison exists.
            Do NOT show "No rival markdowns" or zero pressure here: absence of a
            location is not evidence of no rival activity. */}
        {noLocation && (
          <Card data-testid="deals-no-location" variant="sm" style={{ padding: '20px 22px' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Add a location to begin</div>
            <div style={{ fontSize: 14, color: 'var(--text-1)', fontWeight: 700, marginBottom: 6 }}>
              CannaSpy needs one of your locations before it can compare rival deals.
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 14, maxWidth: 560 }}>
              Deal Patterns shows rival markdown breadth and where a rival now prices below your own menu — both are anchored to one of your locations. Once a location is set up, its tracked rivals' promotions and markdowns appear here.
            </div>
            <Link
              to="/locations"
              data-testid="deals-add-location"
              className="btn btn-primary"
              style={{ textDecoration: 'none', display: 'inline-flex' }}
            >
              Add a location →
            </Link>
          </Card>
        )}

        {/* DECISION SUMMARY — leads with the observed conclusion, its real prior
            movement, its evidence boundary, and one dominant action. Facts lead;
            the recommended action is a separate, subordinate column. */}
        {showDecision && (
          <div data-testid="deals-decision" style={{ marginBottom: 12 }}>
            <DecisionSummary
              className="deal-effectiveness-summary"
              question="Which rival is applying the most pricing pressure?"
              priority={topRival ? 'high' : 'watch'}
              answer={
                topRival ? (
                  <>
                    {topRival.name} is applying the broadest observed pricing pressure:{' '}
                    {ownMenu && topRival.undercut > 0 && <><strong>{fmtInt(topRival.undercut)}</strong> matched own SKUs are undercut across </>}
                    <strong>{fmtInt(topRival.skus)}</strong> markdowns in {topRival.cats.size} categor{topRival.cats.size === 1 ? 'y' : 'ies'}.
                  </>
                ) : (
                  <>No tracked rival is running menu markdowns at {selectedName}. {fmtInt(sum?.active_promotions ?? 0)} marketing promotion{(sum?.active_promotions ?? 0) === 1 ? '' : 's'} are active — no matched price undercut is observed.</>
                )
              }
              change={
                prev && topRival ? (
                  <>
                    Market markdown breadth {breadthChange != null && breadthChange >= 0 ? 'increased' : 'decreased'} by <strong>{fmtInt(Math.abs(breadthChange ?? 0))} SKUs</strong> and{' '}
                    <strong>{fmtInt(Math.abs(undercutChange ?? 0))} more matched own SKUs</strong> are undercut since {prevLabel}.{' '}
                    {prevCluster && topThreat?.category && (
                      <>{topRival.name}'s leading {topThreat.category} cluster {clusterBreadthChange != null && clusterBreadthChange >= 0 ? 'grew by' : 'shrank by'} <strong>{Math.abs(clusterBreadthChange ?? 0)} markdown{Math.abs(clusterBreadthChange ?? 0) === 1 ? '' : 's'}</strong>; its <strong>{fmtInt(topThreat.undercut_you ?? 0)} undercut SKUs</strong> {clusterUndercutChange === 0 ? 'were unchanged' : `changed by ${clusterUndercutChange! > 0 ? '+' : '−'}${Math.abs(clusterUndercutChange!)}`}.</>
                    )}
                  </>
                ) : (
                  <>No comparable previous menu snapshot is available yet, so change over time cannot be shown. The current-state ranking is below.</>
                )
              }
              impact={
                <>
                  This is <strong>breadth of observed price pressure</strong> — how many products a rival discounted and how many you carry it now prices below. “Undercut” counts only confidently matched SKUs you carry, measured against your current menu. <strong>Revenue and margin exposure are unavailable</strong>, and a deep markdown on a SKU you don't carry is not evidence of lost sales.
                </>
              }
              comparison={prev ? { baseline: 'previous menu snapshot', period: prevLabel ? `${obsLabel} vs ${prevLabel}` : undefined } : undefined}
              provenance={{ updatedAt: obsLabel, source: 'tracked rival menus' }}
              action={
                canAssign ? (
                  <button
                    className="btn btn-primary"
                    data-testid="deals-assign-open"
                    onClick={() => { setTaskError(null); setAssignOpen(true) }}
                  >
                    Assign {leadCategory ?? 'pricing'} review →
                  </button>
                ) : (
                  <Link
                    className="btn btn-primary"
                    data-testid="deals-pi-review"
                    to={`/prices?view=priority&location_id=${encodeURIComponent(selectedLocation)}${leadCategory ? `&category=${encodeURIComponent(leadCategory)}` : ''}`}
                    style={{ textDecoration: 'none', display: 'inline-flex' }}
                  >
                    Review in Price Intelligence →
                  </Link>
                )
              }
            >
              {stale && (
                <div data-testid="deals-stale" style={{ marginTop: 9, padding: '7px 10px', border: '1px solid rgba(152,80,0,0.3)', borderRadius: 7, background: 'var(--warning-soft)', color: 'var(--warning)', fontSize: 10.5, fontWeight: 650, lineHeight: 1.4 }}>
                  Latest observation is {ageDays} days old — this reflects {obsLabel}, not current activity.
                </div>
              )}
              <p style={{ fontSize: 11.5, color: 'var(--text-2)', margin: '9px 0 0', lineHeight: 1.45 }}>
                Ranked by SKUs undercutting you, then markdown breadth — not by the deepest single discount.
                {prev && <> Undercut change is measured against your current menu, not a historical own price.</>}
              </p>
            </DecisionSummary>
          </div>
        )}

        {/* HIGHEST CURRENT PRESSURE — the single leading cluster (top of the log),
            stated factually as the bridge into the ranked evidence below. One
            dominant action lives in the summary above; this is not a second CTA. */}
        {showDecision && topThreat && topThreat.kind === 'markdown' && (
          <div data-testid="deals-top-pressure" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: 'linear-gradient(135deg,rgba(212,144,10,0.10),rgba(212,144,10,0.03))', border: '1.5px solid rgba(212,144,10,0.32)', borderRadius: 'var(--r-sm)', padding: '8px 14px', marginBottom: 12 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--warm)', flexShrink: 0 }}>Highest current pressure</span>
            <div style={{ width: 1, height: 15, background: 'rgba(212,144,10,0.35)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--rose)', whiteSpace: 'nowrap' }}>{topThreat.rival}</span>
            {topThreat.category ? <span style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{topThreat.category}</span> : null}
            <span style={{ fontSize: 12.5, color: 'var(--text-2)', minWidth: 0 }}>
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--text-1)' }}>{fmtInt(topThreat.sku_count ?? 0)}</span> SKUs marked down
              {ownMenu && topThreat.undercut_you ? <> · <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--danger)' }}>{fmtInt(topThreat.undercut_you)}</span> undercut you</> : null}
              {topThreat.avg_pct_off != null ? <> · avg <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)' }}>−{topThreat.avg_pct_off}%</span></> : null}
              {topThreat.deepest_pct_off != null ? <> · deepest <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)' }}>−{topThreat.deepest_pct_off}%</span></> : null}
            </span>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>top of the log ↓</span>
          </div>
        )}

        {/* ERROR */}
        {error && (
          <Card variant="sm" style={{ padding: '18px 20px', borderColor: 'var(--danger-soft)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', marginBottom: 6 }}>Couldn't load rival deals.</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginBottom: 12 }}>{error}</div>
            <button onClick={load} style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
          </Card>
        )}

        {/* HONEST DEGRADE — no tracked rivals / no deals (a real location, but
            nothing to show). Distinct from the no-location state above. */}
        {!error && !noLocation && !loading && data && !hasDeals && (
          <Card data-testid="deals-no-deals" variant="sm" style={{ padding: '18px 20px' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>No deals to show</div>
            <div style={{ fontSize: 13, color: 'var(--text-1)', fontWeight: 600, marginBottom: 6 }}>
              {trackedRivals === 0
                ? `No tracked rivals at ${selectedName} yet.`
                : `No active promos or markdowns from your tracked rivals at ${selectedName} right now.`}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {trackedRivals === 0
                ? 'Add a rival to your tracking set at this location to see the deals and markdowns they run.'
                : 'When a tracked rival runs a promotion or marks a SKU below its regular price, it appears here — with the real discount and where it undercuts you.'}
            </div>
          </Card>
        )}

        {/* DEAL LOG TABLE — the full ranked evidence, damage-ordered by default,
            re-sortable by any column, with show-more / show-all. */}
        {!error && !noLocation && (loading || hasDeals) && (
          <Card data-testid="deals-table" style={{ padding: 0, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--warm)" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
              <span style={{ fontSize: 12, fontWeight: 700 }}>Rival Deal Log</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>
                {sortCol === 'damage'
                  ? `${data?.truncated ? `Top ${fmtInt(allDeals.length)} · ` : ''}click any header to re-sort`
                  : 'Sorted by column · click to re-sort'}
              </span>
            </div>

            {loading && !hasDeals
              ? <DataTable density="compact" columns={columns} rows={[]} rowKey={(d) => d.id} style={{ tableLayout: 'fixed' }}
                  empty={<span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>Pulling rival promos and markdowns…</span>} />
              : filteredDeals.length === 0
                ? <DataTable density="compact" columns={columns} rows={[]} rowKey={(d) => d.id} style={{ tableLayout: 'fixed' }}
                    empty={<span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>No deals match these filters.</span>} />
                : (() => {
                    const total = filteredDeals.length
                    const visibleCount = Math.min(5 + logShown, total)
                    return (
                      <>
                        <DataTable
                          density="compact"
                          columns={columns}
                          rows={filteredDeals.slice(0, visibleCount)}
                          rowKey={(d) => d.id}
                          onRowClick={onDealClick}
                          style={{ tableLayout: 'fixed' }}
                        />
                        {total > 5 && (
                          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginRight: 'auto' }}>
                              Showing {fmtInt(visibleCount)} of {fmtInt(total)}
                            </span>
                            {visibleCount < total && (
                              <button data-testid="deals-show-more" onClick={() => setLogShown((n) => n + LOG_STEP)}
                                style={{ fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 700, padding: '5px 13px', borderRadius: 'var(--r-sm)', border: '1.5px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>
                                Show {fmtInt(Math.min(LOG_STEP, total - visibleCount))} more
                              </button>
                            )}
                            {visibleCount < total && (
                              <button data-testid="deals-show-all" onClick={() => setLogShown(total - 5)}
                                style={{ fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 600, padding: '5px 13px', borderRadius: 'var(--r-sm)', border: '1.5px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)', cursor: 'pointer' }}>
                                Show all {fmtInt(total)}
                              </button>
                            )}
                            {logShown > LOG_STEP && (
                              <button onClick={() => setLogShown(LOG_STEP)}
                                style={{ fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 600, padding: '5px 13px', borderRadius: 'var(--r-sm)', border: '1.5px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)', cursor: 'pointer' }}>
                                Show fewer
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )
                  })()}
          </Card>
        )}
      </div>

      {/* ASSIGN-A-TASK DIALOG — files a real annotation against the leading rival
          (entity_type: competitor). Evidence-grounded suggestions, editable body,
          teammate picker, notify choice, saving + error states. */}
      {assignOpen && topThreat && (
        <DealAssignDialog
          rival={topThreat.rival}
          category={leadCategory}
          undercut={topThreat.undercut_you ?? 0}
          skus={topThreat.sku_count ?? 0}
          locationId={selectedLocation || null}
          saving={taskSaving}
          error={taskError}
          onCancel={() => { setAssignOpen(false); setTaskError(null) }}
          onSubmit={submitAssign}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: '11px 18px', fontSize: 12, fontWeight: 600, boxShadow: '0 8px 30px rgba(0,0,0,0.15)', zIndex: 9000, display: 'flex', alignItems: 'center', gap: 8, maxWidth: 460 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--warm)', flexShrink: 0 }} />
          {toast}
        </div>
      )}
    </div>
  )
}

// ── Assign-a-task dialog ──────────────────────────────────────────────────────
// Files against the LEADING rival competitor. Suggested wordings are computed
// from the leading cluster's real numbers — evidence only, no fabricated claim.
function DealAssignDialog({
  rival, category, undercut, skus, locationId, saving, error, onCancel, onSubmit,
}: {
  rival: string
  category: string | null
  undercut: number
  skus: number
  locationId: string | null
  saving: boolean
  error: string | null
  onCancel: () => void
  onSubmit: (body: string, opts: { assigneeUserId: string | null; notify: NotifyOption }) => void
}) {
  const cat = category ?? 'pricing'
  const suggestions = [
    `Review our matched ${cat} prices against ${rival} — ${fmtInt(undercut)} of our SKUs are currently undercut across ${fmtInt(skus)} rival markdowns.`,
    `Prioritize a ${cat} pricing pass — ${rival} leads current observed markdown pressure.`,
  ]
  const [body, setBody] = useState(suggestions[0])
  const { assignees, loading: assigneesLoading } = useEligibleAssignees(locationId)
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [notify, setNotify] = useState<NotifyOption>('none')
  const formError = validateAssignForm({ notify, assigneeUserId: assigneeId })

  return createPortal(
    <div
      data-testid="assign-scrim"
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        data-testid="assign-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)', maxHeight: '86vh', overflow: 'auto', background: 'var(--surface-solid, var(--surface))', border: '1px solid var(--border-2)', borderRadius: 'var(--r-md)', boxShadow: '0 20px 60px rgba(0,0,0,0.28)', padding: 18 }}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 4 }}>Assign a task</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }}>{cat === 'pricing' ? 'Pricing' : cat} review · {rival}</div>
        </div>

        <SectionLabel style={{ marginBottom: 7 }}>Task</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              data-testid="assign-suggestion"
              onClick={() => setBody(s)}
              style={{
                textAlign: 'left', padding: '7px 10px', borderRadius: 'var(--r-sm)', fontSize: 12, fontFamily: 'var(--sans)', cursor: 'pointer',
                border: `1.5px solid ${body === s ? 'var(--accent)' : 'var(--border)'}`,
                background: body === s ? 'var(--surface-3, var(--surface-2))' : 'transparent', color: 'var(--text-1)',
              }}
            >
              {s}
            </button>
          ))}
        </div>

        <SectionLabel style={{ marginBottom: 7 }}>Or write your own</SectionLabel>
        <textarea
          data-testid="assign-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          style={{ width: '100%', boxSizing: 'border-box', marginBottom: 12, padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)', fontSize: 12, fontFamily: 'var(--sans)', resize: 'vertical' }}
        />

        <div data-testid="assign-assignee" style={{ marginBottom: 12 }}>
          <MemberPicker assignees={assignees} value={assigneeId} onChange={setAssigneeId} loading={assigneesLoading} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <NotifySelect value={notify} onChange={setNotify} />
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginBottom: 16, fontFamily: 'var(--sans)' }}>
          Shows up on Team Annotations and Today. Notifications go only to the assigned teammate.
        </div>

        {error && (
          <div data-testid="assign-error" style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--danger)', color: 'var(--danger)', fontSize: 11.5, fontFamily: 'var(--sans)' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" data-testid="assign-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            data-testid="assign-save"
            disabled={saving || !body.trim() || !!formError}
            title={formError ?? undefined}
            onClick={() => onSubmit(body.trim(), { assigneeUserId: assigneeId, notify })}
            style={{ opacity: saving || !body.trim() || !!formError ? 0.5 : 1 }}
          >
            {saving ? 'Saving…' : 'Create task'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
